/**
 * Leaderboard Page
 * Steam-based leaderboards with Mythic banner
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Trophy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MythicBanner from '../components/leaderboard/MythicBanner.jsx';
import {
  isSteamAvailable,
  getLeaderboardEntries,
  getCurrentMythicHolder,
  LEADERBOARD_NAMES,
} from '../lib/steam/leaderboards.js';

// Leaderboard options for the dropdown
const LEADERBOARD_OPTIONS = [
  { key: 'NET_WORTH', label: 'Net Worth', format: 'currency' },
  { key: 'OPERATIONAL_GAINS', label: 'Daily Earnings (OAG)', format: 'currency' },
  { key: 'TOTAL_FLIGHTS', label: 'Total Flights', format: 'number' },
  { key: 'TOTAL_PASSENGERS', label: 'Total Passengers', format: 'number' },
  { key: 'RARE_AIRCRAFT', label: 'Collectibles', format: 'number' },
];

function formatScore(value, format) {
  if (value === null || value === undefined) return '-';

  if (format === 'currency') {
    const absAmount = Math.abs(value);
    if (absAmount >= 1000000000) return '$' + (value / 1000000000).toFixed(1) + 'B';
    if (absAmount >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'M';
    if (absAmount >= 1000) return '$' + (value / 1000).toFixed(0) + 'K';
    return '$' + value.toFixed(0);
  }

  return value.toLocaleString();
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [mythicClaim, setMythicClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState('NET_WORTH');
  const [steamAvailable, setSteamAvailable] = useState(false);

  // Check Steam availability on mount
  useEffect(() => {
    setSteamAvailable(isSteamAvailable());
  }, []);

  // Fetch leaderboard data
  const loadData = async () => {
    setLoading(true);
    setError(null);

    if (!isSteamAvailable()) {
      setError('Steam not available. Leaderboards require Steam.');
      setLoading(false);
      return;
    }

    try {
      const boardName = LEADERBOARD_NAMES[selectedBoard];

      const [leaderboardResult, mythicResult] = await Promise.all([
        getLeaderboardEntries(boardName, 'Global', 0, 99),
        getCurrentMythicHolder(),
      ]);

      if (!leaderboardResult.success) {
        setError(leaderboardResult.error || 'Failed to load leaderboard');
        console.error('[Leaderboard] Error:', leaderboardResult.error);
      } else {
        // Transform Steam entries to our format
        const transformed = (leaderboardResult.entries || []).map((entry, index) => ({
          id: entry.steamId || index,
          rank: entry.rank || index + 1,
          username: entry.steamName || entry.personaName || 'Unknown',
          score: entry.score,
        }));
        setEntries(transformed);
      }

      // Transform mythic holder to expected format
      if (mythicResult.holder && mythicResult.isValid) {
        setMythicClaim({
          username: mythicResult.holder.steamName,
          claimed_at: new Date(mythicResult.holder.rolledAt).toISOString(),
        });
      } else {
        setMythicClaim(null);
      }
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error('[Leaderboard] Exception:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when board changes
  useEffect(() => {
    loadData();
  }, [selectedBoard]);

  const currentBoardConfig = LEADERBOARD_OPTIONS.find(b => b.key === selectedBoard);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: '#0a0a0a',
        color: '#e0e0e0',
        padding: '20px',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/OpsCenter')}
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft size={20} />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Trophy className="text-yellow-400" size={24} />
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>
              Leaderboard
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Leaderboard selector */}
          <select
            value={selectedBoard}
            onChange={(e) => setSelectedBoard(e.target.value)}
            style={{
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '6px',
              padding: '6px 12px',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {LEADERBOARD_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>

          <Button
            variant="ghost"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="text-gray-400 hover:text-white"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Mythic Banner */}
      <MythicBanner mythicClaim={mythicClaim} />

      {/* Leaderboard Categories Info */}
      <div
        style={{
          padding: '16px 20px',
          background: 'rgba(96, 255, 208, 0.05)',
          border: '1px solid rgba(96, 255, 208, 0.2)',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div style={{ color: '#60ffd0', fontSize: '20px' }}>🎮</div>
        <p style={{ color: '#a3a3a3', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
          Leaderboards track two separate categories: players who earned everything through gameplay
          and players who purchased cash packs. Your pure gameplay achievements are never mixed with
          purchased progress - compete on your own terms.
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div
          style={{
            padding: '20px',
            background: '#1a0a0a',
            border: '1px solid #4a1a1a',
            borderRadius: '8px',
            color: '#ef4444',
            marginBottom: '20px',
          }}
        >
          {error}
        </div>
      )}

      {/* Leaderboard Table */}
      <div
        style={{
          background: '#0c0c0c',
          border: '1px solid #1a1a1a',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading...
          </div>
        ) : (
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{
                  width: '60px',
                  padding: '12px 8px',
                  textAlign: 'center',
                  borderBottom: '1px solid #2a2a2a',
                  color: '#888',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                }}>
                  #
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '1px solid #2a2a2a',
                  color: '#888',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                }}>
                  USERNAME
                </th>
                <th style={{
                  width: '150px',
                  padding: '12px 8px',
                  textAlign: 'right',
                  borderBottom: '2px solid #ffd700',
                  color: '#ffd700',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                }}>
                  {currentBoardConfig?.label || 'SCORE'}
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#666',
                      fontStyle: 'italic'
                    }}
                  >
                    No entries yet. Start playing to get on the board!
                  </td>
                </tr>
              ) : (
                entries.map((entry, index) => (
                  <tr
                    key={entry.id}
                    style={{
                      background: index % 2 === 0 ? '#0a0a0a' : '#0f0f0f',
                    }}
                  >
                    <td style={{
                      padding: '10px 8px',
                      textAlign: 'center',
                      borderBottom: '1px solid #1a1a1a',
                      color: '#60ffd0',
                      fontWeight: '600',
                    }}>
                      {entry.rank}
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      textAlign: 'left',
                      borderBottom: '1px solid #1a1a1a',
                      color: '#fff',
                      fontWeight: '600',
                    }}>
                      {entry.username}
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      textAlign: 'right',
                      borderBottom: '1px solid #1a1a1a',
                      color: '#ffd700',
                      fontWeight: '600',
                      fontSize: '15px',
                      textShadow: '0 0 10px rgba(255, 215, 0, 0.3)',
                    }}>
                      {formatScore(entry.score, currentBoardConfig?.format)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: '20px',
          fontSize: '12px',
          color: '#444',
          textAlign: 'center',
        }}
      >
        {steamAvailable ? 'Powered by Steam Leaderboards' : 'Steam required for leaderboards'}
      </div>
    </div>
  );
}
